import { X, Truck, ArrowRight, MoveRight, PackageCheck, Package } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { 
  updateOrdersStatusByShipment,
  getWarehouses, 
  updateShipmentStatusOnly,
  updateOrdersWarehouseStatus,
  updateDevRouteDt,
  updateDevolucionDt,
  updateAppointment,
  updateReentregaAta,
  updateReentregaRamp,
  getShipmentLoadingPhaseInfo,
  advanceLoadingPhase
} from '../../api'; // Updated imports to include loading phase functions
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { DisabledByDefault } from '@mui/icons-material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import Alert from '@mui/material/Alert';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
// Importar los componentes necesarios para el DateTimePicker
import dayjs from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Modificar la definición del componente para recibir shipment como prop
const ModalEstatus = ({ onClose, shipment, refreshShipments }) => { // Simplificar el nombre del prop
  console.log("ModalEstatus recibió:", shipment);
  
  const [embarque, setEmbarque] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paradaActual, setParadaActual] = useState(null); // Estado para la parada actual
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false); // Estado para el modal de bodega
  const [warehouses, setWarehouses] = useState([]); // Estado para las bodegas
  const [selectedWarehouse, setSelectedWarehouse] = useState(null); // Estado para la bodega seleccionada
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false); // Estado para el modal de nueva cita
  // Modificar para usar dayjs
  const [newAppointment, setNewAppointment] = useState(null); // Estado para la nueva cita
  
  // Nuevos estados para la confirmación
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newSubstatus, setNewSubstatus] = useState('');

  // Nuevos estados para la funcionalidad de múltiples orígenes
  const [loadingPhaseInfo, setLoadingPhaseInfo] = useState(null);
  const [showLoadingPhaseDialog, setShowLoadingPhaseDialog] = useState(false);
  const [isMultiOriginLoading, setIsMultiOriginLoading] = useState(false);
  const [originTransitionConfirm, setOriginTransitionConfirm] = useState(false);

  // Efecto para establecer la parada actual cuando se recibe un embarque inicial
  useEffect(() => {
    console.log("useEffect activado con shipment:", shipment);
    if (shipment) {
      setEmbarque(shipment);
      
      // Verificar si tiene órdenes antes de intentar determinar la parada
      if (shipment.orders && shipment.orders.length > 0) {
        console.log("Órdenes en el embarque:", shipment.orders);
        const parada = determinarParadaActual(shipment.orders);
        setParadaActual(parada);
      } else {
        console.log("El embarque no tiene órdenes o la propiedad orders no existe");
        setParadaActual(null);
      }

      // Cargar información de la fase de carga si estamos en ARRIBADO
      if (shipment.status === 'ARRIBADO') {
        fetchLoadingPhaseInfo(shipment.id);
      }
    } else {
      console.log("No se recibió ningún embarque");
    }
  }, [shipment]);

  // Función para obtener la información de fases de carga del embarque
  const fetchLoadingPhaseInfo = async (shipmentId) => {
    try {
      setIsMultiOriginLoading(true);
      const phaseInfo = await getShipmentLoadingPhaseInfo(shipmentId);
      setLoadingPhaseInfo(phaseInfo);
      setIsMultiOriginLoading(false);
    } catch (error) {
      console.error("Error al obtener información de fases de carga:", error);
      setIsMultiOriginLoading(false);
    }
  };

  // Función para avanzar a la siguiente fase de carga (siguiente origen)
  const handleAdvanceToNextOrigin = async () => {
    try {
      setIsMultiOriginLoading(true);
      
      // Primero, actualizar las órdenes del origen actual con el estado "LIBERADO" 
      // para asegurarse que la métrica order_origin_chargefin se registre correctamente
      if (loadingPhaseInfo && loadingPhaseInfo.current_origin) {
        console.log(`Marcando órdenes de ${loadingPhaseInfo.current_origin} como LIBERADO antes de avanzar`);
        
        // Primero actualizamos el estado de las órdenes del origen actual a LIBERADO
        await actualizarOrdenesAsociadas(
          embarque.id, 
          'ARRIBADO', 
          'LIBERADO',
          null,
          loadingPhaseInfo.current_origin 
        );
        
        toast.info(`Se finalizó la carga en ${loadingPhaseInfo.current_origin}`);

        // Actualizar el estado del embarque a TRANSITO/CARGA sin afectar órdenes
        await updateShipmentStatusOnly(embarque.id, {
          status: 'TRANSITO',
          Substatus: 'CARGA'
        });

        toast.info(`Embarque en tránsito al siguiente origen de carga`);
      }
      
      // Ahora avanzamos a la siguiente fase en la base de datos
      const result = await advanceLoadingPhase(embarque.id);
      
      // Actualizar la información de fase
      await fetchLoadingPhaseInfo(embarque.id);
      
      // NOTA: Eliminamos el código que automáticamente marcaba el embarque como ARRIBADO/PENDIENTE
      // y actualizaba las órdenes del siguiente origen. Ahora el usuario debe realizar esta acción
      // manualmente al llegar físicamente al siguiente origen.
      
      toast.success(`Se ha avanzado a la fase de carga ${result.current_phase}. El embarque se encuentra en tránsito hacia ${result.current_origin || 'el siguiente origen'}`);
      setOriginTransitionConfirm(false);
      setShowLoadingPhaseDialog(false);
      
      // Si hay un cambio en el embarque o sus órdenes, actualizar
      if (refreshShipments) {
        refreshShipments();
      }
      
      // Cerrar el modal principal con un pequeño retraso para que el usuario vea el mensaje de éxito
      setTimeout(() => {
        onClose();
      }, 1500);
      
      setIsMultiOriginLoading(false);
    } catch (error) {
      console.error("Error al avanzar la fase de carga:", error);
      toast.error("Error al avanzar a la siguiente fase de carga");
      setIsMultiOriginLoading(false);
    }
  };

  // Función para determinar la parada actual basada en el estado de las órdenes
  const determinarParadaActual = (orders) => {
    if (!orders || orders.length === 0) return null;

    // Obtener el número máximo de paradas
    const maxParada = Math.max(...orders.map((order) => order.Stop));

    // Recorrer las paradas en orden
    for (let stop = 1; stop <= maxParada; stop++) {
      const ordenesParada = orders.filter((order) => order.Stop === stop);

      // Verificar si todas las órdenes de la parada están finalizadas
      const todasFinalizadas = ordenesParada.every(
        (order) => order.Order_status === 'FINALIZADO'
      );

      // Si no todas están finalizadas, esta es la parada actual
      if (!todasFinalizadas) {
        return stop;
      }
    }

    // Si todas las paradas están finalizadas, retornar null
    return null;
  };

  // Función para actualizar el estado de las órdenes asociadas
  const actualizarOrdenesAsociadas = async (shipmentId, newStatus, newSubstatus, paradaActual = null, currentOrigin = null) => {
    try {
      console.log(`Actualizando órdenes: Estado=${newStatus}, Subestado=${newSubstatus}, Origen=${currentOrigin}`);
      
      // Crear una variable para almacenar los IDs de órdenes a actualizar
      let ordenesAActualizar = [];
      
      // Si estamos marcando como ARRIBADO, considerar la fase actual de carga
      if (newStatus === 'ARRIBADO') {
        // Si tenemos un origen actual definido y estamos en un flujo de múltiples orígenes
        if (currentOrigin && loadingPhaseInfo && loadingPhaseInfo.current_phase > 0) {
          // Filtrar órdenes por origen Y que su loading_order coincida con la fase actual
          ordenesAActualizar = embarque.orders
            .filter(order => 
              order.Origin === currentOrigin && 
              order.loading_order === loadingPhaseInfo.current_phase
            )
            .map(order => order.id);
          
          if (ordenesAActualizar.length > 0) {
            console.log(`Se actualizarán ${ordenesAActualizar.length} órdenes con loading_order=${loadingPhaseInfo.current_phase} del origen ${currentOrigin}`);
            await updateOrdersStatusByShipment(
              shipmentId, 
              newStatus, 
              newSubstatus, 
              paradaActual, 
              ordenesAActualizar
            );
            toast.success(`Se actualizaron ${ordenesAActualizar.length} órdenes de ${currentOrigin}.`);
            return;
          } else {
            console.warn(`No se encontraron órdenes con loading_order=${loadingPhaseInfo.current_phase} para el origen ${currentOrigin}`);
          }
        } else if (loadingPhaseInfo && loadingPhaseInfo.current_phase > 0) {
          // Si no hay origen específico, filtrar todas las órdenes con loading_order igual a la fase actual
          ordenesAActualizar = embarque.orders
            .filter(order => order.loading_order === loadingPhaseInfo.current_phase)
            .map(order => order.id);
          
          if (ordenesAActualizar.length > 0) {
            console.log(`Se actualizarán ${ordenesAActualizar.length} órdenes con loading_order=${loadingPhaseInfo.current_phase}`);
            await updateOrdersStatusByShipment(
              shipmentId, 
              newStatus, 
              newSubstatus, 
              paradaActual, 
              ordenesAActualizar
            );
            toast.success(`Se actualizaron ${ordenesAActualizar.length} órdenes con loading_order=${loadingPhaseInfo.current_phase}.`);
            return;
          } else {
            console.warn(`No se encontraron órdenes con loading_order=${loadingPhaseInfo.current_phase}`);
          }
        }
      } else if (currentOrigin) {
        // Para otros estados, si tenemos un origen específico, seguir con la lógica actual
        ordenesAActualizar = embarque.orders
          .filter(order => order.Origin === currentOrigin)
          .map(order => order.id);
        
        if (ordenesAActualizar.length > 0) {
          await updateOrdersStatusByShipment(
            shipmentId, 
            newStatus, 
            newSubstatus, 
            paradaActual, 
            ordenesAActualizar
          );
          toast.success(`Se actualizaron ${ordenesAActualizar.length} órdenes de ${currentOrigin}.`);
          return;
        } else {
          console.warn(`No se encontraron órdenes para el origen ${currentOrigin}`);
        }
      }
      
      // Si no se aplicaron los casos especiales o no se encontraron órdenes, seguir con el comportamiento normal
      await updateOrdersStatusByShipment(shipmentId, newStatus, newSubstatus, paradaActual);
    } catch (error) {
      console.error('Error al actualizar órdenes asociadas:', error);
      toast.error('Error al actualizar las órdenes asociadas.');
    }
  };

  // Modificar la función handleButtonClick para mostrar el diálogo de confirmación
  const handleButtonClick = async () => {
    if (!embarque) return;

    let statusToUpdate = '';
    let substatusToUpdate = '';

    // Lógica para determinar el siguiente estatus y subestatus
    if (embarque.status === 'PENDIENTE' && embarque.Substatus === 'CREADO') {
      statusToUpdate = 'ARRIBADO';
      substatusToUpdate = 'PENDIENTE';
    } else if (embarque.status === 'TRANSITO' && embarque.Substatus === 'CARGA') {
      // Nuevo caso: permitir marcar arribo al siguiente origen de carga
      statusToUpdate = 'ARRIBADO';
      substatusToUpdate = 'PENDIENTE';
    } else if (embarque.status === 'ARRIBADO' && embarque.Substatus === 'PENDIENTE') {
      statusToUpdate = 'ARRIBADO';
      substatusToUpdate = 'CARGANDO';
    } else if (embarque.status === 'ARRIBADO' && embarque.Substatus === 'CARGANDO') {
      // Si tenemos múltiples orígenes y no estamos en la última fase
      if (loadingPhaseInfo && 
          loadingPhaseInfo.current_phase > 0 &&
          loadingPhaseInfo.current_phase < loadingPhaseInfo.total_phases) {
        // Mostrar el diálogo para gestionar la transición entre orígenes
        setShowLoadingPhaseDialog(true);
        return;
      } else {
        // Comportamiento estándar si no hay múltiples orígenes o es la última fase
        statusToUpdate = 'ARRIBADO';
        substatusToUpdate = 'LIBERADO';
      }
    } else if (embarque.status === 'ARRIBADO' && embarque.Substatus === 'LIBERADO') {
      statusToUpdate = 'TRANSITO';
      substatusToUpdate = 'EN RUTA';
    } else if (embarque.status === 'TRANSITO' && embarque.Substatus === 'EN RUTA') {
      statusToUpdate = 'EN CLIENTE';
      substatusToUpdate = 'PENDIENTE';    } else if (embarque.status === 'EN CLIENTE' && embarque.Substatus === 'PENDIENTE') {
      statusToUpdate = 'EN CLIENTE';
      substatusToUpdate = 'DESCARGANDO';
    }else if (embarque.status === 'FINALIZADO' && embarque.Substatus === 'PARCIAL') {
      setIsWarehouseModalOpen(true); // Abrir el modal de selección de bodega
      return;
    } else {
      toast.error('Acción no disponible para este embarque.');
      return;
    }

    // Guardar los valores para usar en la confirmación
    setNewStatus(statusToUpdate);
    setNewSubstatus(substatusToUpdate);
    
    // Mostrar el diálogo de confirmación
    setConfirmDialogOpen(true);
  };

  // Nueva función para manejar la confirmación y aplicar los cambios
  const handleConfirmStatusChange = async () => {
    try {
      setLoading(true);
      
      // Si estamos en estado ARRIBADO con substatus LIBERADO y hay más fases de carga
      if (newStatus === 'ARRIBADO' && newSubstatus === 'LIBERADO' && 
          loadingPhaseInfo && loadingPhaseInfo.current_phase < loadingPhaseInfo.total_phases) {
        
        // Primero actualizamos el estado de las órdenes del origen actual
        if (loadingPhaseInfo.current_origin) {
          await actualizarOrdenesAsociadas(
            embarque.id, 
            newStatus, 
            newSubstatus, 
            null, // No hay parada específica
            loadingPhaseInfo.current_origin // Pasar el origen actual, no los IDs
          );
        }
        
        // Luego cambiamos el embarque a EN TRANSITO con substatus CARGA
        await updateShipmentStatusOnly(embarque.id, {
          status: 'TRANSITO',
          Substatus: 'CARGA'
        });
        
        // Avanzamos a la siguiente fase de carga
        await advanceLoadingPhase(embarque.id);
        
        // Actualizamos la información de fase de carga
        await fetchLoadingPhaseInfo(embarque.id);
        
        toast.success('Carga finalizada. Embarque en tránsito al siguiente origen de carga.');
      } else {
        // Comportamiento normal para otros estados
        const response = await updateShipmentStatusOnly(embarque.id, {
          status: newStatus,
          Substatus: newSubstatus,
        });

        if (response) {
          setEmbarque(response);

          // Actualizar las órdenes asociadas según el tipo de cambio de estado
          if (newStatus === 'ARRIBADO') {
            // Si es ARRIBADO, verificar si hay múltiples orígenes y solo actualizar órdenes del origen actual
            if (loadingPhaseInfo && loadingPhaseInfo.current_phase > 0 && loadingPhaseInfo.current_origin) {
              await actualizarOrdenesAsociadas(
                embarque.id, 
                newStatus, 
                newSubstatus, 
                null, // No hay parada específica
                loadingPhaseInfo.current_origin // Pasar el nombre del origen actual
              );
            } else {
              // Si no hay múltiples orígenes, actualizar todas
              await actualizarOrdenesAsociadas(embarque.id, newStatus, newSubstatus);
            }
          } else if (newStatus === 'TRANSITO' && newSubstatus === 'EN RUTA') {
            // Si es TRANSITO EN RUTA, afectar a todas las órdenes del embarque
            await actualizarOrdenesAsociadas(embarque.id, newStatus, newSubstatus);
          } else if (newStatus === 'EN CLIENTE') {
            // Cambios por parada: afectar solo las órdenes de la parada actual
            await actualizarOrdenesAsociadas(embarque.id, newStatus, newSubstatus, paradaActual);
          }

          toast.success('Embarque y órdenes actualizados correctamente.');
          
          // Si pasamos a ARRIBADO, cargar información de fases de carga
          if (newStatus === 'ARRIBADO') {
            await fetchLoadingPhaseInfo(embarque.id);
          }
        } else {
          toast.error('Error al actualizar el embarque.');
        }
      }
      
      // Actualizar la lista de embarques en la página principal
      if (refreshShipments) {
        refreshShipments();
      }
      
      // Cerrar el diálogo de confirmación
      setConfirmDialogOpen(false);
      
      // Cerrar el modal principal con un pequeño retraso para que el usuario vea el mensaje de éxito
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error al actualizar el embarque:', error);
      toast.error('Error de conexión al servidor.');
      setConfirmDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar la selección de bodega y guardar
  const handleSaveWarehouse = async () => {
    console.log('handleSaveWarehouse called'); // Debugging line
    if (!selectedWarehouse) {
      toast.error('Por favor, selecciona una bodega.');
      return;
    }

    try {
      setLoading(true);
      console.log('Selected Warehouse:', selectedWarehouse); // Debugging line
      console.log('Embarque Orders:', embarque.orders); // Debugging line

      // Actualizar las órdenes asociadas con substatus "PARCIAL"
      const ordersToUpdate = embarque.orders.filter(order => order.order_Substatus === 'PARCIAL' || order.order_Substatus === 'RECHAZADO');
      console.log('Orders to update:', ordersToUpdate); // Debugging line
      
      await updateOrdersWarehouseStatus(
        ordersToUpdate.map(order => order.id),
        selectedWarehouse.nombre,
        'TRANSITO'
      );

      // Actualizar el timestamp en la tabla metrics
      await updateDevRouteDt(ordersToUpdate.map(order => order.id));

      toast.success('Órdenes actualizadas correctamente.');
      setIsWarehouseModalOpen(false); // Cerrar el modal de selección de bodega
      
      // Actualizar la lista de embarques en la página principal
      if (refreshShipments) {
        refreshShipments();
      }
      
      // Cerrar el modal principal después de guardar exitosamente
      setTimeout(() => {
        onClose();
      }, 1500); // Pequeño retraso para que el usuario vea el mensaje de éxito
    } catch (error) {
      console.error('Error:', error); // Debugging line
      toast.error('Error de conexión al servidor.');
    } finally {
      setLoading(false);
    }
  };

  
  // Función para manejar el clic en el botón "Devuelto"
  const handleDevuelto = async () => {
    try {
      setLoading(true);
      const ordersToUpdate = embarque.orders.filter(order => order.dev_status === 'TRANSITO');
      
      await updateOrdersWarehouseStatus(
        ordersToUpdate.map(order => order.id),
        null,
        'DEVUELTO'
      );

      // Actualizar el timestamp en la tabla metrics
      await updateDevolucionDt(ordersToUpdate.map(order => order.id));

      // Actualizar el estado del embarque
      const updatedOrders = embarque.orders.map(order => {
        if (order.dev_status === 'TRANSITO') {
          return { ...order, dev_status: 'DEVUELTO' };
        }
        return order;
      });

      setEmbarque({ ...embarque, orders: updatedOrders });

      toast.success('Órdenes marcadas como devueltas correctamente.');
      
      // Actualizar la lista de embarques en la página principal
      if (refreshShipments) {
        refreshShipments();
      }
      
      // Cerrar el modal principal después de marcar como devuelto exitosamente
      setTimeout(() => {
        onClose();
      }, 1500); // Pequeño retraso para que el usuario vea el mensaje de éxito
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al servidor.');
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener las bodegas del backend
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const data = await getWarehouses();
        setWarehouses(data);
      } catch (error) {
        console.error('Error fetching warehouses:', error);
      }
    };
    fetchWarehouses();
  }, []);

  // Calcular el número total de piezas rechazadas
  const totalPiezasRechazadas = embarque
    ? embarque.orders
        .filter(order => order.order_Substatus === 'PARCIAL' || order.order_Substatus === 'RECHAZO')
        .reduce((total, order) => {
          const piezasRechazadas = order.productos
            ? order.productos.reduce((subtotal, producto) => subtotal + (producto.piezas_rechazadas || 0), 0)
            : 0;
          return total + piezasRechazadas;
        }, 0)
    : 0;

  const handleRetornoAlmacen = async () => {
    try {
      setLoading(true);
      const ordersToUpdate = embarque.orders.filter(order => order.order_Substatus === 'RECHAZADO');
      
      await updateOrdersWarehouseStatus(
        ordersToUpdate.map(order => order.id),
        null,
        'PENDIENTE'
      );

      // Actualizar el estado del embarque
      const updatedOrders = embarque.orders.map(order => {
        if (order.order_Substatus === 'RECHAZADO') {
          return { ...order, dev_status: 'PENDIENTE' };
        }
        return order;
      });

      setEmbarque({ ...embarque, orders: updatedOrders });

      toast.success('Órdenes marcadas como pendientes para retorno a almacén correctamente.');
      
      // Actualizar la lista de embarques en la página principal
      if (refreshShipments) {
        refreshShipments();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al servidor.');
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar la selección de nueva cita y guardar
  const handleSaveAppointment = async () => {
    if (!newAppointment) {
      toast.error('Por favor, selecciona una nueva cita.');
      return;
    }

    try {
      setLoading(true);
      // Formatear la fecha seleccionada con dayjs al formato ISO
      const formattedDate = newAppointment.format('YYYY-MM-DDTHH:mm');
      
      // Lógica para actualizar la cita de las órdenes rechazadas
      const ordersToUpdate = embarque.orders.filter(order => order.order_Substatus === 'RECHAZADO');
      
      await updateAppointment(
        ordersToUpdate.map(order => order.id),
        formattedDate
      );

      toast.success('Cita actualizada correctamente.');
      setIsAppointmentModalOpen(false); // Cerrar el modal de nueva cita
      
      // Actualizar la lista de embarques en la página principal
      if (refreshShipments) {
        refreshShipments();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrarLlegada = async () => {
    if (!embarque) return;

    try {
      setLoading(true);
      const ordersToUpdate = embarque.orders.filter(order => order.order_Substatus === 'RECHAZADO');
      
      await updateReentregaAta(ordersToUpdate.map(order => order.id));

      // Actualizar el estado del embarque
      const updatedOrders = embarque.orders.map(order => {
        if (order.order_Substatus === 'RECHAZADO') {
          return { ...order, reentrega_dt: new Date().toISOString() };
        }
        return order;
      });

      setEmbarque({ ...embarque, orders: updatedOrders });

      toast.success('Llegada registrada correctamente.');
      
      // Actualizar la lista de embarques en la página principal
      if (refreshShipments) {
        refreshShipments();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handlerampareentrega = async () => {
    if (!embarque) return;

    try {
      setLoading(true);
      const ordersToUpdate = embarque.orders.filter(order => order.order_Substatus === 'RECHAZADO' && order.substatus_reentrega === 'EN CLIENTE');
        await updateReentregaRamp(ordersToUpdate.map(order => order.id));

      // Actualizar el estado del embarque
      const updatedOrders = embarque.orders.map(order => {
        if (order.order_Substatus === 'RECHAZADO' && order.substatus_reentrega === 'EN CLIENTE') {
          return { ...order, substatus_reentrega: 'DESCARGANDO' };
        }
        return order;
      });

      setEmbarque({ ...embarque, orders: updatedOrders });

      toast.success('Estado actualizado a "Descargando" correctamente.');
      
      // Actualizar la lista de embarques en la página principal
      if (refreshShipments) {
        refreshShipments();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al servidor.');
    } finally {
      setLoading(false);
    }
  };

  // Agrupar órdenes por origen para mostrar en el diálogo de múltiples orígenes
  const ordersByOrigin = embarque && embarque.orders ? 
    embarque.orders.reduce((acc, order) => {
      const origin = order.Origin || 'Sin origen';
      if (!acc[origin]) {
        acc[origin] = [];
      }
      acc[origin].push(order);
      return acc;
    }, {}) : {};

  // Función para obtener el nombre del siguiente origen basándose en la secuencia de carga
  const getNextOriginName = (embarque, phaseInfo) => {
    // Si ya tenemos el next_origin en la info de fase, lo usamos directamente
    if (phaseInfo.next_origin) {
      return phaseInfo.next_origin;
    }
    
    // Si no, calculamos el siguiente origen basado en loading_sequence y current_loading_phase
    if (embarque.loading_sequence) {
      const nextPhase = (embarque.current_loading_phase || 0) + 1;
      for (const [origin, phase] of Object.entries(embarque.loading_sequence)) {
        if (phase === nextPhase) {
          return origin;
        }
      }
    }
    
    // Si no podemos obtener el nombre específico, devolvemos un valor genérico
    return phaseInfo.current_origin ? `siguiente origen (fase ${(embarque.current_loading_phase || 0) + 1})` : 'siguiente origen';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-md relative">
        {/* Botón de cierre en la esquina superior derecha */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        >
          <X size={24} />
        </button>

        <h2 className="text-lg font-semibold mb-4">Actualizar Estado</h2>

        {/* Mostrar información del embarque si se encuentra */}
        {!embarque ? (
          <div className="p-4 text-center text-red-500">
            No se ha recibido información del embarque.
          </div>
        ) : (
          <div className="mt-4 p-4 border rounded-md bg-gray-100">
            <h3 className="text-md font-semibold">Detalles del Embarque</h3>
            <p><strong>ID:</strong> {embarque.id}</p>
            <p><strong>Referencia:</strong> {embarque.reference}</p>
            <p><strong>Truck:</strong> {embarque.Truck || 'N/A'}</p>
            <p><strong>Origen:</strong> {embarque.origin || 'N/A'}</p>
            <p><strong>Destino:</strong> {embarque.destination || 'N/A'}</p>
            <p><strong>Estado:</strong> {embarque.status || 'N/A'}</p>
            <p><strong>Subestado:</strong> {embarque.Substatus || 'N/A'}</p>
            
            {/* Información de diagnóstico - Comentada pero disponible para uso futuro
            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-300 rounded-md">
              <h4 className="font-semibold text-yellow-800">Información de Diagnóstico</h4>
              {loadingPhaseInfo && (
                <div className="mt-1">
                  <p><strong>current_loading_phase:</strong> {loadingPhaseInfo.current_phase}</p>
                  <p><strong>total_loading_phases:</strong> {loadingPhaseInfo.total_phases}</p>
                  <p><strong>current_origin:</strong> {loadingPhaseInfo.current_origin || 'No definido'}</p>
                </div>
              )}
              
              {embarque.orders && embarque.orders.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium">Órdenes por Origen y Loading Order:</p>
                  <div className="max-h-60 overflow-y-auto mt-1">
                    <table className="w-full text-xs border-collapse">
                      <thead className="bg-yellow-100">
                        <tr>
                          <th className="border border-yellow-300 px-1 py-1">ID</th>
                          <th className="border border-yellow-300 px-1 py-1">Orden</th>
                          <th className="border border-yellow-300 px-1 py-1">Origen</th>
                          <th className="border border-yellow-300 px-1 py-1">Loading Order</th>
                          <th className="border border-yellow-300 px-1 py-1">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {embarque.orders.map(order => (
                          <tr key={order.id} className={loadingPhaseInfo && order.loading_order === loadingPhaseInfo.current_phase ? "bg-green-100" : ""}>
                            <td className="border border-yellow-300 px-1 py-1">{order.id}</td>
                            <td className="border border-yellow-300 px-1 py-1">{order.Order_number}</td>
                            <td className="border border-yellow-300 px-1 py-1">{order.Origin || 'N/A'}</td>
                            <td className="border border-yellow-300 px-1 py-1">{order.loading_order || 'N/A'}</td>
                            <td className="border border-yellow-300 px-1 py-1">{order.Order_status}/{order.order_Substatus}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {embarque.loading_sequence && (
                <div className="mt-2">
                  <p className="font-medium">Secuencia de Carga (loading_sequence):</p>
                  <div className="border border-yellow-300 rounded p-1 bg-white mt-1 text-xs">
                    <pre className="whitespace-pre-wrap">
                      {JSON.stringify(embarque.loading_sequence, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
            */}
            
            {/* Mostrar información de fase de carga si existe */}
            {loadingPhaseInfo && loadingPhaseInfo.total_phases > 1 && (
              <div className="mt-2 p-2 bg-blue-50 rounded-md">
                <p className="font-medium text-blue-800 mb-1">Secuencia de Carga</p>
                <p className="text-sm">
                  <strong>Fase actual:</strong> {loadingPhaseInfo.current_phase} de {loadingPhaseInfo.total_phases}
                </p>
                <p className="text-sm">
                  <strong>Origen actual:</strong> {loadingPhaseInfo.current_origin || 'No establecido'}
                </p>
                {loadingPhaseInfo.next_origin && (
                  <p className="text-sm"><strong>Siguiente origen:</strong> {loadingPhaseInfo.next_origin}</p>
                )}
                
                <Stepper 
                  activeStep={loadingPhaseInfo.current_phase - 1} 
                  alternativeLabel 
                  className="mt-2"
                >
                  {Array.from({length: loadingPhaseInfo.total_phases}, (_, i) => (
                    <Step key={i}>
                      <StepLabel>Fase {i + 1}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </div>
            )}
            
            {/* Mostrar información de órdenes si existen */}
            {embarque.orders && embarque.orders.length > 0 ? (
              <>
                <p><strong>Órdenes:</strong> {embarque.orders.length}</p>
                
                {/* Resto del código para mostrar estados de devolución */}
                {embarque.orders.some(order => order.dev_status === 'TRANSITO') && (
                  <p><strong>Estado de Devolución:</strong> en transito a {embarque.orders.find(order => order.dev_status === 'TRANSITO').bodega_dev}</p>
                )}
                {embarque.orders.some(order => order.appointment_try) && (
                  <p><strong>Fecha de reintento:</strong> {embarque.orders.find(order => order.appointment_try).appointment_try}</p>
                )}
              </>
            ) : (
              <p><strong>Órdenes:</strong> Sin órdenes asociadas</p>
            )}
          </div>
        )}

        {/* Mostrar error si ocurre */}
        {error && <p className="text-red-500 mt-2">{error}</p>}

        {/* Botón dinámico según estatus y subestatus */}
        <div className="mt-4 flex justify-center">
          {/* Botón "Ruta a Almacén" */}
          {embarque && !(
            embarque.status === 'FINALIZADO' && 
            embarque.Substatus === 'PARCIAL' && 
            (embarque.orders.some(order => order.dev_status === 'TRANSITO') || embarque.orders.some(order => order.dev_status === 'DEVUELTO'))
          ) && !(
            embarque.status === 'FINALIZADO' && 
            embarque.Substatus === 'RECHAZADO'
          ) && (
            <button
              onClick={handleButtonClick}              className={`bg-green-600 text-white p-2 rounded-md hover:bg-green-700 ${
                embarque.status === 'EN CLIENTE' && embarque.Substatus === 'DESCARGANDO'
                  ? 'hidden'
                  : ''
              }`}
              disabled={loading || !embarque || embarque.Substatus === 'DESCARGANDO'}
            >
              {embarque.status === 'PENDIENTE' && embarque.Substatus === 'CREADO'
                ? loadingPhaseInfo?.current_origin 
                  ? `Llegada a ${loadingPhaseInfo.current_origin}` 
                  : embarque.loading_sequence && Object.keys(embarque.loading_sequence).length > 0
                    ? `Llegada a ${Object.entries(embarque.loading_sequence).find(([_, fase]) => fase === 1)?.[0] || 'Origen'}`
                    : embarque.origin 
                      ? `Llegada a ${embarque.origin}` 
                      : 'Llegada a Origen'
: embarque.status === 'TRANSITO' && embarque.Substatus === 'CARGA'
                ? (() => {
                    console.log("Determinando el siguiente origen con:", embarque);
                    
                    // Primero intentamos usar la información directamente del embarque
                    if (embarque.current_loading_phase && embarque.loading_sequence) {
                      const nextPhase = embarque.current_loading_phase;
                      console.log("Buscando fase:", nextPhase, "en secuencia:", embarque.loading_sequence);
                      
                      // Encontrar qué origen corresponde a la fase actual
                      for (const [origin, phase] of Object.entries(embarque.loading_sequence)) {
                        if (phase === nextPhase) {
                          console.log("¡Origen encontrado para la fase actual!", origin);
                          return `Llegada a ${origin}`;
                        }
                      }
                    }
                    
                    // Si aún no hemos retornado, probar con el current_origin del embarque
                    if (embarque.current_origin) {
                      console.log("Usando current_origin del embarque:", embarque.current_origin);
                      return `Llegada a ${embarque.current_origin}`;
                    }
                    
                    // Intentar usar loadingPhaseInfo como último recurso
                    if (loadingPhaseInfo && loadingPhaseInfo.current_origin) {
                      console.log("Usando loadingPhaseInfo.current_origin:", loadingPhaseInfo.current_origin);
                      return `Llegada a ${loadingPhaseInfo.current_origin}`;
                    } else if (loadingPhaseInfo && loadingPhaseInfo.next_origin) {
                      console.log("Usando loadingPhaseInfo.next_origin:", loadingPhaseInfo.next_origin);
                      return `Llegada a ${loadingPhaseInfo.next_origin}`;
                    }
                    
                    console.log("No se pudo determinar el nombre del origen, usando texto genérico");
                    return 'Llegada a siguiente origen';
                  })()
                : embarque.status === 'ARRIBADO' && embarque.Substatus === 'PENDIENTE'
                ? 'Iniciar Carga'
                : embarque.status === 'ARRIBADO' && embarque.Substatus === 'CARGANDO' && loadingPhaseInfo && loadingPhaseInfo.current_phase < loadingPhaseInfo.total_phases
                ? `Finalizar Carga en ${loadingPhaseInfo.current_origin || 'origen actual'}`
                : embarque.status === 'ARRIBADO' && embarque.Substatus === 'CARGANDO'
                ? 'Liberar Embarque'
                : embarque.status === 'ARRIBADO' && embarque.Substatus === 'LIBERADO'
                ? 'Poner en Tránsito'
                : embarque.status === 'TRANSITO' && embarque.Substatus === 'EN RUTA'
                ? `Registrar Llegada (Parada ${paradaActual})`                : embarque.status === 'EN CLIENTE' && embarque.Substatus === 'PENDIENTE'
                ? 'Descargando'
                : embarque.status === 'FINALIZADO' && embarque.Substatus === 'PARCIAL'
                ? 'Ruta a Almacén'
                : 'Actualizar Estado'}
            </button>
          )}
          {/* Botón "Devuelto" */}
          {embarque && embarque.status === 'FINALIZADO' && (embarque.Substatus === 'PARCIAL' || embarque.Substatus === 'RECHAZADO') && embarque.orders.some(order => order.dev_status === 'TRANSITO') && (
            <button
              onClick={handleDevuelto}
              className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 ml-2"
              disabled={loading}
            >
              Devuelto
            </button>
          )}
          {/* Botones "Reintentar entrega" y "Retorno a almacén" */}

          {embarque && embarque.status === 'FINALIZADO' && embarque.Substatus === 'RECHAZADO' && 
            embarque.orders.some(order => !order.appointment_try && !order.substatus_reentrega && !order.dev_status) && (
            <>
              <button
                onClick={() => setIsAppointmentModalOpen(true)} // Abrir el modal de nueva cita
                className="bg-yellow-600 text-white p-2 rounded-md hover:bg-yellow-700 ml-2"
                disabled={loading}
              >
                Reintentar entrega
              </button>
              <button
                onClick={handleRetornoAlmacen}
                className="bg-red-600 text-white p-2 rounded-md hover:bg-red-700 ml-2"
                disabled={loading}
              >
                Retorno a almacén
              </button>
            </>
          )}
          {/* Botón "Registrar llegada" */}
          {embarque && embarque.status === 'FINALIZADO' && embarque.Substatus === 'RECHAZADO' && 
            embarque.orders.some(order => order.appointment_try && !order.substatus_reentrega) && (
            <button
              onClick={handleRegistrarLlegada}
              className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 ml-2"
              disabled={loading}
            >
              Registrar llegada
            </button>
          )}
            {/* Botón "Descargando" */}
          {embarque && embarque.status === 'FINALIZADO' && embarque.Substatus === 'RECHAZADO' && 
            embarque.orders.some(order => order.appointment_try && order.substatus_reentrega === 'EN CLIENTE') && (
            <button
              onClick={handlerampareentrega}
              className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 ml-2"
              disabled={loading}
            >
              Descargando
            </button>
          )}
          
          {/* Botón "seleccion de bodega" */}
          {embarque && embarque.status === 'FINALIZADO' && embarque.Substatus === 'RECHAZADO' && 
            embarque.orders.some(order => order.dev_status === 'PENDIENTE' ) && (
            <button
            onClick={() => setIsWarehouseModalOpen(true)}
              className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 ml-2"
              disabled={loading}
            >
              Ruta a Almacen
            </button>
          )}
        </div>

        {/* Diálogo de confirmación */}
        <Dialog
          open={confirmDialogOpen}
          onClose={(event, reason) => {
            // Prevenir cierre al hacer clic fuera del diálogo
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              setConfirmDialogOpen(false);
            }
          }}
          disableEscapeKeyDown
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title" className="text-center font-bold">
            {"Confirmar cambio de estado"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <div className="text-center mb-4">
                ¿Estás seguro de cambiar el embarque <strong className="text-blue-600">{embarque?.reference}</strong>?
              </div>
              
              <div className="flex items-center justify-center my-6">
                <div className="text-center px-3 py-2 border rounded-lg bg-gray-100">
                  <div className="font-semibold">{embarque?.status}</div>
                  <div className="text-sm text-gray-700">{embarque?.Substatus}</div>
                </div>
                
                <div className="mx-4 flex items-center animate-pulse">
                  <Truck size={24} className="text-blue-600 mr-2" />
                  <ArrowRight size={20} className="text-blue-600" />
                </div>
                
                <div className="text-center px-3 py-2 border rounded-lg bg-green-100">
                  <div className="font-semibold text-green-700">{newStatus}</div>
                  <div className="text-sm text-green-600">{newSubstatus}</div>
                </div>
              </div>
            </DialogContentText>
          </DialogContent>
          <DialogActions className="pb-4 px-4">
            <Button 
              onClick={() => setConfirmDialogOpen(false)} 
              color="inherit"
              variant="outlined"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmStatusChange} 
              color="primary" 
              variant="contained"
              autoFocus
            >
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de selección de bodega */}
        <Modal open={isWarehouseModalOpen} onClose={() => setIsWarehouseModalOpen(false)}>
          <Box className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-md mx-auto mt-20">
            <h2 className="text-lg font-semibold mb-4">Seleccionar Almacen de Devolución</h2>
            <Autocomplete
              fullWidth
              options={warehouses}
              getOptionLabel={(option) => option.nombre}
              onChange={(event, value) => setSelectedWarehouse(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bodegas"
                  margin="normal"
                  required
                />
              )}
            />
            <div className="mt-4 flex justify-end space-x-2">
              <Button onClick={() => setIsWarehouseModalOpen(false)} variant="outlined">
                Cancelar
              </Button>
              <Button onClick={handleSaveWarehouse} variant="contained" color="primary">
                Guardar
              </Button>
            </div>
          </Box>
        </Modal>

        {/* Modal de selección de nueva cita - Actualizado para usar DateTimePicker */}
        <Modal open={isAppointmentModalOpen} onClose={() => setIsAppointmentModalOpen(false)}>
          <Box className="bg-white p-6 rounded-lg shadow-lg w-11/12 max-w-md mx-auto mt-20">
            <h2 className="text-lg font-semibold mb-4">Seleccionar Nueva Cita</h2>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Nueva Cita"
                value={newAppointment}
                onChange={(newValue) => setNewAppointment(newValue)}
                ampm={false} // Usar formato de 24 horas
                minutesStep={5}
                format="DD/MM/YYYY HH:mm"
                slotProps={{ 
                  textField: { 
                    fullWidth: true,
                    required: true,
                    margin: "normal"
                  } 
                }}
              />
            </LocalizationProvider>
            <div className="mt-4 flex justify-end space-x-2">
              <Button onClick={() => setIsAppointmentModalOpen(false)} variant="outlined">
                Cancelar
              </Button>
              <Button onClick={handleSaveAppointment} variant="contained" color="primary">
                Guardar
              </Button>
            </div>
          </Box>
        </Modal>

        {/* Nuevo diálogo para gestionar la transición entre orígenes de carga */}
        <Dialog
          open={showLoadingPhaseDialog}
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              setShowLoadingPhaseDialog(false);
            }
          }}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
          aria-labelledby="loading-phase-dialog-title"
        >
          <DialogTitle id="loading-phase-dialog-title" className="text-center font-bold">
            Secuencia de Carga por Múltiples Orígenes
          </DialogTitle>
          <DialogContent>
            {isMultiOriginLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
              </Box>
            ) : (
              <>
                {loadingPhaseInfo && (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 3 }}
                    icon={<Package size={24} />}
                  >
                    Fase actual: {loadingPhaseInfo.current_phase} de {loadingPhaseInfo.total_phases} 
                    &nbsp;|&nbsp; Origen: <strong>{loadingPhaseInfo.current_origin || 'No definido'}</strong>
                  </Alert>
                )}

                {originTransitionConfirm ? (
                  <Box sx={{ my: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <div className="font-semibold">¿Estás seguro de que deseas avanzar al siguiente origen?</div>
                      <div className="text-sm mt-1">
                        Esta acción moverá el embarque a la siguiente fase de carga.
                      </div>
                    </Alert>
                    
                    <div className="flex items-center justify-center my-3">
                      <div className="text-center px-3 py-2 border rounded-lg bg-blue-100">
                        <div className="font-medium text-blue-800">{loadingPhaseInfo?.current_origin || 'Origen actual'}</div>
                        <div className="text-xs text-blue-600">Fase {loadingPhaseInfo?.current_phase}</div>
                      </div>
                      
                      <div className="mx-3 flex items-center">
                        <MoveRight size={20} className="text-blue-600" />
                      </div>
                      
                      <div className="text-center px-3 py-2 border rounded-lg bg-green-100">
                        <div className="font-medium text-green-800">{loadingPhaseInfo?.next_origin || 'Siguiente origen'}</div>
                        <div className="text-xs text-green-600">Fase {loadingPhaseInfo?.current_phase + 1}</div>
                      </div>
                    </div>
                  </Box>
                ) : (
                  <Box sx={{ my: 2 }}>
                    <p className="mb-3">
                      Este embarque tiene configurada una secuencia de carga con múltiples orígenes:
                    </p>
                    
                    <div className="grid gap-2">
                      {Object.entries(ordersByOrigin).map(([origin, orders], idx) => (
                        <div 
                          key={origin} 
                          className={`p-2 border rounded-md ${
                            loadingPhaseInfo?.current_origin === origin 
                              ? 'bg-blue-50 border-blue-300' 
                              : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                              loadingPhaseInfo?.current_origin === origin 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-200'
                            }`}>
                              {idx + 1}
                            </div>
                            <span className="font-medium">{origin}</span>
                            {loadingPhaseInfo?.current_origin === origin && (
                              <span className="ml-2 px-2 py-0.5 bg-blue-200 text-blue-800 text-xs rounded">
                                Actual
                              </span>
                            )}
                          </div>
                          <div className="ml-8 text-sm text-gray-600 mt-1">
                            {orders.length} órdenes
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <Alert severity="info" sx={{ mt: 3 }} icon={<PackageCheck size={24} />}>
                      <div className="font-medium">¿Ha finalizado la carga en {loadingPhaseInfo?.current_origin || 'el origen actual'}?</div>
                      <div className="text-sm mt-1">
                        Al confirmar, el sistema avanzará a la siguiente fase de carga en {loadingPhaseInfo?.next_origin || 'el siguiente origen'}.
                      </div>
                    </Alert>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions className="pb-4 px-4">
            <Button 
              onClick={() => {
                setOriginTransitionConfirm(false);
                setShowLoadingPhaseDialog(false);
              }} 
              color="inherit"
              variant="outlined"
            >
              Cancelar
            </Button>
            
            {originTransitionConfirm ? (
              <Button 
                onClick={handleAdvanceToNextOrigin} 
                color="primary" 
                variant="contained"
                disabled={isMultiOriginLoading}
              >
                {isMultiOriginLoading ? 'Procesando...' : 'Confirmar avance'}
              </Button>
            ) : (
              <Button 
                onClick={() => setOriginTransitionConfirm(true)} 
                color="primary" 
                variant="contained"
              >
                Continuar
              </Button>
            )}
          </DialogActions>
        </Dialog>
        
        <ToastContainer />
      </div>
    </div>
  );
};

export default ModalEstatus;